<template>
    <b-modal
        :id="id"
        :title="title"
        centered
        no-close-on-backdrop
        @hidden="close"
    >
        <div class="mb-4">
            <label for="user">User</label>
            <v-select
                id="user"
                v-model="selectedUser"
                label="fullName"
                :options="users"
                placeholder="Search..."
                :class="{
                    'is-invalid': hasError('selectedUserId')
                }"
                :filterable="false"
                @search="onUserSearch"
            >
                <template slot="no-options">
                    Start typing to search for a user
                </template>
            </v-select>

            <div class="invalid-feedback">
                {{ getError('selectedUserId') }}
            </div>
        </div>

        <div>
            <label for="paymentMethod">Payment method</label>

            <v-select
                id="paymentMethod"
                v-model="form.paymentMethod"
                :options="paymentMethods"
                :class="{ 'is-invalid': hasError('paymentMethod') }"
                placeholder="Payment method"
            />

            <div class="invalid-feedback">
                {{ getError('paymentMethod') }}
            </div>
        </div>

        <template #modal-footer>
            <div class="col-12 d-flex justify-content-between">
                <button
                    type="button"
                    class="btn btn-danger waves-effect waves-light mr-2"
                    @click="close"
                >
                    Close
                </button>
                <button
                    type="submit"
                    class="btn btn-success waves-effect waves-light"
                    @click="add"
                >
                    Send
                </button>
            </div>
        </template>
    </b-modal>
</template>

<script>
import { StatusCodes as HTTP } from 'http-status-codes';
import { mapActions, mapGetters } from 'vuex';
import { required } from 'vuelidate/lib/validators';

export default {
    props: {
        id: {
            type: String,
            required: false,
            default: 'course-add-user'
        },
        courseId: {
            type: String,
            required: true
        }
    },

    data() {
        return {
            users: [],
            timer: null,
            serverErrors: [],
            selectedUser: null,
            form: {
                selectedUserId: null,
                paymentMethod: null
            },
            entry: {},
            title: 'Add user to the marathon'
        };
    },

    computed: {
        ...mapGetters({
            paymentMethods: 'courses/PAYMENT_METHODS'
        })
    },

    watch: {
        selectedUser() {
            if (this.selectedUser) {
                this.form.selectedUserId = this.selectedUser.id;
            }
        }
    },

    validations: {
        form: {
            selectedUserId: {
                required
            },

            paymentMethod: {
                required
            }
        }
    },

    methods: {
        ...mapActions({
            getUsers: 'users/index',
            addUser: 'courses/addUser'
        }),

        async onUserSearch(search, loading) {
            if (!search.length) {
                this.users = [];

                return;
            }

            if (this.timer) {
                clearTimeout(this.timer);
                this.timer = null;
            }

            loading(true);

            this.timer = setTimeout(async () => {
                const { rows: users } = await this.getUsers({
                    search
                });

                this.users = users;

                loading(false);
            }, 500);
        },

        async add() {
            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }

            const data = {
                id: this.courseId,
                userId: this.form.selectedUserId,
                paymentMethod: this.form.paymentMethod
            };

            try {
                await this.addUser(data);

                this.$emit('added');

                this.$toaster('User has been added!');
                this.close();
            } catch (error) {
                console.error(error);

                const { response } = error;
                let errorMessage = 'User could not be added';

                if (response) {
                    const { data, status } = response;

                    if (status === HTTP.BAD_REQUEST && data?.errors) {
                        this.serverErrors = data.errors;

                        errorMessage = 'Recheck your form';
                    }

                    if (status === HTTP.UNPROCESSABLE_ENTITY && data) {
                        errorMessage = data;
                    }
                }

                this.$toasterError(errorMessage);
            }
        },

        close() {
            this.$bvModal.hide(this.id);
        },

        hasError(key) {
            return (
                this.$v.form[key].$error ||
                this.serverErrors.find(error => error.param === key)
            );
        },

        getError(key) {
            return this.$getValidationErrorMessageHandler.handle(
                this.$v.form,
                this.serverErrors,
                key
            );
        }
    }
};
</script>
